import { formatDate } from "../../shared/utils/format";
import { planState } from "../../shared/utils/subscription";
import type { SubscriptionRow } from "./types";

export interface TimelineRow {
  label: string;
  value: string;
}

type TimelineSub = Pick<
  SubscriptionRow,
  | "provider"
  | "status"
  | "startDate"
  | "endDate"
  | "nextBillingDate"
  | "cancelAtPeriodEnd"
  | "nextPlan"
  | "trialStartAt"
  | "trialEndAt"
  | "trialEndedReason"
>;

const TRIAL_ENDED: Record<string, string> = {
  CONVERTED: "converted to paid",
  USER_CANCEL: "cancelled by user",
  PAYMENT_FAILED: "payment failed",
  PLAN_CHANGE: "changed plan",
};

const PAID_STATUSES = new Set(["active", "past_due"]);

const trialRow = (sub: TimelineSub): TimelineRow => {
  const reason = sub.trialEndedReason ? ` (${TRIAL_ENDED[sub.trialEndedReason] ?? sub.trialEndedReason})` : "";
  return { label: "Free trial", value: `${formatDate(sub.trialStartAt)} → ${formatDate(sub.trialEndAt)}${reason}` };
};

const inTrialRows = (sub: TimelineSub): TimelineRow[] =>
  sub.cancelAtPeriodEnd
    ? [
        { label: "Access until", value: formatDate(sub.trialEndAt) },
        { label: "Then", value: sub.nextPlan ?? "Free plan" },
      ]
    : [
        { label: "Paid plan starts", value: formatDate(sub.trialEndAt) },
        { label: "First charge", value: formatDate(sub.nextBillingDate ?? sub.trialEndAt) },
      ];

const paidPeriodRows = (sub: TimelineSub, now: number): TimelineRow[] => {
  switch (planState(sub, now)) {
    case "active":
      return [{ label: "Next billing", value: formatDate(sub.nextBillingDate ?? sub.endDate) }];
    case "ending":
      return [
        { label: "Access until", value: formatDate(sub.endDate) },
        { label: "Then", value: sub.nextPlan ?? "Free plan" },
      ];
    case "overdue":
      return [{ label: "Renewal was due", value: formatDate(sub.endDate) }];
    case "past_due":
      return [{ label: "Payment due since", value: formatDate(sub.nextBillingDate ?? sub.endDate) }];
    default:
      return [{ label: "Ended on", value: formatDate(sub.endDate) }];
  }
};

/** The subscription's life in order: trial, when the paid plan starts, then billing. */
export const planTimeline = (sub: TimelineSub, now: number = Date.now()): TimelineRow[] => {
  if (sub.provider === "free") {
    return [
      { label: "Started", value: formatDate(sub.startDate) },
      { label: "Next billing", value: "None (Free plan)" },
    ];
  }
  if (!sub.trialEndAt) {
    return [{ label: "Plan started", value: formatDate(sub.startDate) }, ...paidPeriodRows(sub, now)];
  }
  if (sub.status === "trialing") return [trialRow(sub), ...inTrialRows(sub)];

  const converted = sub.trialEndedReason === "CONVERTED" || PAID_STATUSES.has(sub.status);
  if (!converted) return [trialRow(sub)];
  return [trialRow(sub), { label: "Paid plan started", value: formatDate(sub.trialEndAt) }, ...paidPeriodRows(sub, now)];
};
